/**
 * Vertical meal list — the default layout on a phone.
 *
 * The horizontal carousel is the approved artwork and is kept as the alternate
 * mode, but it shows one and a bit cards at a time, so comparing five meals
 * means swiping back and forth. A vertical list shows three at once and
 * scrolls the way the rest of the screen already does.
 *
 * Same data, same selection state and the same card content as the carousel —
 * only the arrangement differs, which is what lets the toggle switch between
 * them without refetching or losing the selected meal.
 */

import { memo } from 'react';
import { Image, Pressable, StyleSheet, View } from 'react-native';

import { formatMoney, type Meal } from '@mfa/domain';
import { mealImageSource } from '../../assets';
import { colors, radius, sizing, space } from '../../theme';
import { CheckIcon } from '../common/Icon';
import { Skeleton } from '../feedback/Skeleton';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export interface MealListProps {
  meals: Meal[];
  selectedId: string | null;
  onSelect: (meal: Meal) => void;
  loading?: boolean;
  testID?: string;
}

export const MealList = memo(function MealList({
  meals,
  selectedId,
  onSelect,
  loading = false,
  testID = 'meal-list',
}: MealListProps) {
  const t = useT();
  const pick = useLocalized();
  if (loading) {
    return (
      <View style={styles.list} testID={`${testID}-loading`}>
        {[0, 1, 2].map((key) => (
          <Skeleton key={key} height={sizing.mealRowHeight} style={styles.skeleton} />
        ))}
      </View>
    );
  }

  return (
    <View style={styles.list} testID={testID}>
      {meals.map((meal) => {
        const selected = meal.id === selectedId;
        const source = mealImageSource(meal.imageUrl, meal.name);
        return (
          <Pressable
            key={meal.id}
            onPress={() => onSelect(meal)}
            accessibilityRole="radio"
            accessibilityState={{ selected }}
            accessibilityLabel={`${pick(meal.name, meal.nameUr)}. ${formatMoney(meal.unitPrice)} per person. ${pick(meal.description, meal.descriptionUr)}`}
            testID={`${testID}-item-${meal.id}`}
            style={[styles.row, selected && styles.rowSelected]}
          >
            <View style={styles.thumbWrap}>
              {source ? (
                <Image source={source} style={styles.thumb} accessibilityIgnoresInvertColors />
              ) : (
                <View style={[styles.thumb, styles.thumbFallback]} />
              )}
            </View>

            <View style={styles.body}>
              <Text variant="cardTitle" numberOfLines={2}>
                {pick(meal.name, meal.nameUr)}
              </Text>
              <Text variant="caption" tone="secondary" numberOfLines={2}>
                {pick(meal.description, meal.descriptionUr)}
              </Text>
              {/* Separate nodes, not nested: a nested Text merges into one
                  string and the price stops being addressable on its own. */}
              <View style={styles.priceRow}>
                <Text variant="amountSmall" tone="brand">
                  {formatMoney(meal.unitPrice)}
                </Text>
                <Text variant="caption" tone="secondary">{t('Per Person')}</Text>
              </View>
            </View>

            {selected ? (
              <View style={styles.check}>
                <CheckIcon size={24} color={colors.status.success.solid} />
              </View>
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
});

const styles = StyleSheet.create({
  list: {
    gap: space.md,
  },
  skeleton: {
    borderRadius: radius.lg,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    padding: space.sm,
    borderRadius: radius.lg,
    backgroundColor: colors.bg.surface,
    borderWidth: 1,
    borderColor: colors.border.default,
  },
  rowSelected: {
    borderColor: colors.border.brand,
    backgroundColor: colors.bg.selected,
  },
  thumbWrap: {
    borderRadius: radius.md,
    overflow: 'hidden',
  },
  thumb: {
    width: sizing.mealRowThumb,
    height: sizing.mealRowThumb,
    resizeMode: 'cover',
  },
  thumbFallback: {
    backgroundColor: colors.bg.surfaceSunken,
  },
  body: {
    flex: 1,
    gap: space.xxs,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: space.xs,
  },
  check: {
    paddingRight: space.xs,
  },
});
